import React from 'react';
import { SlimHeader } from './components/headers/SlimHeader.jsx';
import { EnterpriseHeader } from './components/headers/EnterpriseHeader.jsx';
import { CenteredBrandHeader } from './components/headers/CenteredBrandHeader.jsx';

import { PortalSearchHero } from './components/heroes/PortalSearchHero.jsx';
import { HighlightGridHero } from './components/heroes/HighlightGridHero.jsx';
import { SplitFeaturedHero } from './components/heroes/SplitFeaturedHero.jsx';

import { SpecsHeavyProductCard } from './components/cards/SpecsHeavyProductCard.jsx';
import { CompactRowProductCard } from './components/cards/CompactRowProductCard.jsx';
import { VisualProductCard } from './components/cards/VisualProductCard.jsx';

import { SidebarFilterLayout } from './components/filters/SidebarFilterLayout.jsx';
import { TopbarFilterLayout } from './components/filters/TopbarFilterLayout.jsx';

import { CopilotDrawerFlyout } from '../components/copilot/CopilotDrawerFlyout.jsx';
import { CopilotFloatingFab } from '../components/copilot/CopilotFloatingFab.jsx';

/**
 * Slot Registry Dictionary
 */
export const COMPONENT_REGISTRY = {
  header: {
    slim: SlimHeader,
    enterprise: EnterpriseHeader,
    centered: CenteredBrandHeader,
  },
  hero: {
    portal_search: PortalSearchHero,
    highlight_grid: HighlightGridHero,
    split_featured: SplitFeaturedHero,
  },
  card: {
    specs_heavy: SpecsHeavyProductCard,
    compact_row: CompactRowProductCard,
    visual_card: VisualProductCard,
  },
  filter: {
    sidebar: SidebarFilterLayout,
    topbar: TopbarFilterLayout,
  },
  copilot: {
    drawer_flyout: CopilotDrawerFlyout,
    floating_fab: CopilotFloatingFab,
  },
};

/**
 * Standard Defaults per Slot
 */
export const DEFAULT_SLOT_VARIANTS = {
  header: 'slim',
  hero: 'portal_search',
  card: 'specs_heavy',
  filter: 'sidebar',
  copilot: 'drawer_flyout',
};

/**
 * Normalizes slot type names to canonical keys
 */
export function normalizeSlotType(slotType) {
  if (!slotType || typeof slotType !== 'string') return 'unknown';
  const clean = slotType.trim().toLowerCase();
  if (!clean) return 'unknown';
  if (clean === 'product_card' || clean === 'productcard' || clean === 'cards' || clean === 'product_cards') {
    return 'card';
  }
  if (clean === 'headers') return 'header';
  if (clean === 'heroes') return 'hero';
  if (clean === 'filters') return 'filter';
  if (clean === 'copilot' || clean === 'copilots' || clean === 'assistant' || clean === 'alwis') {
    return 'copilot';
  }
  return clean;
}

/**
 * Normalizes variant aliases to canonical variant names
 */
export function normalizeVariantName(slotType, variant) {
  if (!variant || typeof variant !== 'string') return null;
  const clean = variant.trim().toLowerCase();
  if (!clean) return null;
  const canonicalSlot = normalizeSlotType(slotType);

  if (canonicalSlot === 'card') {
    if (clean === 'specs' || clean === 'specs_heavy' || clean === 'technical') return 'specs_heavy';
    if (clean === 'compact' || clean === 'compact_row' || clean === 'row' || clean === 'table') return 'compact_row';
    if (clean === 'visual' || clean === 'visual_card' || clean === 'high_visual') return 'visual_card';
  }

  if (canonicalSlot === 'hero') {
    if (clean === 'portal' || clean === 'portal_search' || clean === 'search') return 'portal_search';
    if (clean === 'highlight' || clean === 'highlight_grid' || clean === 'grid') return 'highlight_grid';
    if (clean === 'split' || clean === 'split_featured' || clean === 'featured') return 'split_featured';
  }

  if (canonicalSlot === 'header') {
    if (clean === 'slim' || clean === 'modern') return 'slim';
    if (clean === 'enterprise' || clean === 'mega_menu' || clean === 'megamenu') return 'enterprise';
    if (clean === 'centered' || clean === 'centered_brand' || clean === 'brand') return 'centered';
  }

  if (canonicalSlot === 'filter') {
    if (clean === 'sidebar' || clean === 'left') return 'sidebar';
    if (clean === 'topbar' || clean === 'top' || clean === 'horizontal') return 'topbar';
  }

  if (canonicalSlot === 'copilot') {
    if (clean === 'drawer' || clean === 'drawer_flyout' || clean === 'flyout' || clean === 'sidebar') return 'drawer_flyout';
    if (clean === 'fab' || clean === 'floating' || clean === 'floating_fab' || clean === 'button') return 'floating_fab';
  }

  return clean;
}

/**
 * Resolves the active variant string for a given slot with fallback
 */
export function resolveSlotVariant(slotType, requestedVariant) {
  const canonicalSlot = normalizeSlotType(slotType);
  const normalizedVariant = normalizeVariantName(canonicalSlot, requestedVariant);
  const slotVariants = COMPONENT_REGISTRY[canonicalSlot];

  if (
    slotVariants &&
    normalizedVariant &&
    Object.prototype.hasOwnProperty.call(slotVariants, normalizedVariant)
  ) {
    return normalizedVariant;
  }

  return DEFAULT_SLOT_VARIANTS[canonicalSlot] || 'default';
}

/**
 * Resolves the React component for a given slot and variant
 */
export function resolveSlotComponent(slotType, requestedVariant) {
  const canonicalSlot = normalizeSlotType(slotType);
  const resolvedVariant = resolveSlotVariant(canonicalSlot, requestedVariant);
  const slotVariants = COMPONENT_REGISTRY[canonicalSlot];

  if (
    slotVariants &&
    Object.prototype.hasOwnProperty.call(slotVariants, resolvedVariant)
  ) {
    return slotVariants[resolvedVariant];
  }

  // Fallback to default
  const defaultVar = DEFAULT_SLOT_VARIANTS[canonicalSlot];
  if (
    slotVariants &&
    defaultVar &&
    Object.prototype.hasOwnProperty.call(slotVariants, defaultVar)
  ) {
    return slotVariants[defaultVar];
  }

  return null;
}

/**
 * Dynamic registration helper for custom partner components
 */
export function registerSlotComponent(slotType, variantName, Component) {
  if (!variantName || typeof variantName !== 'string') return;
  const canonicalSlot = normalizeSlotType(slotType);
  if (!COMPONENT_REGISTRY[canonicalSlot]) {
    COMPONENT_REGISTRY[canonicalSlot] = {};
  }
  COMPONENT_REGISTRY[canonicalSlot][variantName.trim().toLowerCase()] = Component;
}

/**
 * Slot Dispatcher Component
 */
export function ComponentRegistrySlot({ slot, variant, fallback = null, ...props }) {
  const Component = resolveSlotComponent(slot, variant);
  if (!Component) {
    return fallback ? React.createElement(fallback, props) : null;
  }
  return React.createElement(Component, props);
}

export const ComponentRegistry = {
  get: resolveSlotComponent,
  resolve: resolveSlotComponent,
  resolveVariant: resolveSlotVariant,
  register: registerSlotComponent,
  registry: COMPONENT_REGISTRY,
  defaults: DEFAULT_SLOT_VARIANTS,
  Slot: ComponentRegistrySlot,
};
